import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const PAD = 8;
const GAP = 8;

function viewportLimits() {
  return {
    maxW: Math.max(120, window.innerWidth - PAD * 2),
    maxH: Math.max(80, window.innerHeight - PAD * 2),
  };
}

export function ViewportTooltip({
  children,
  content,
  contentClassName = '',
  className = '',
}: {
  children: ReactNode;
  content: ReactNode;
  contentClassName?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false, maxW: 320, maxH: 240 });

  const place = useCallback(() => {
    const btn = btnRef.current;
    const tip = tipRef.current;
    if (!btn || !tip) return;
    const { maxW, maxH } = viewportLimits();
    tip.style.maxWidth = `${maxW}px`;
    tip.style.maxHeight = `${maxH}px`;
    const r = btn.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    const maxLeft = Math.max(PAD, window.innerWidth - PAD - tw);
    let left = r.left + r.width / 2 - tw / 2;
    left = Math.min(Math.max(left, PAD), maxLeft);
    let top = r.bottom + GAP;
    if (top + th > window.innerHeight - PAD) {
      top = r.top - GAP - th;
    }
    const maxTop = Math.max(PAD, window.innerHeight - PAD - th);
    top = Math.min(Math.max(top, PAD), maxTop);
    setPos({ top, left, ready: true, maxW, maxH });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos((p) => (p.ready ? { ...p, top: 0, left: 0, ready: false } : p));
      return;
    }
    place();
    const on = () => place();
    window.addEventListener('scroll', on, true);
    window.addEventListener('resize', on);
    return () => {
      window.removeEventListener('scroll', on, true);
      window.removeEventListener('resize', on);
    };
  }, [open, place, content]);

  return (
    <span
      ref={btnRef}
      className={`relative inline-flex shrink-0 align-middle ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      {children}
      {open
        ? createPortal(
            <div
              ref={tipRef}
              role="tooltip"
              className={`fixed z-[20000] overflow-auto break-words ${contentClassName}`}
              style={{
                top: pos.top,
                left: pos.left,
                visibility: pos.ready ? 'visible' : 'hidden',
                maxWidth: pos.maxW,
                maxHeight: pos.maxH,
              }}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

export function HintTooltip({ children, widthClass = 'w-64' }: { children: ReactNode; widthClass?: string }) {
  return (
    <ViewportTooltip
      content={children}
      contentClassName={`${widthClass} rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-left text-[11px] font-normal normal-case leading-relaxed tracking-normal text-slate-700 shadow-xl`}
    >
      <span
        tabIndex={0}
        className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-slate-300 bg-white text-[10px] font-bold text-slate-500 outline-none"
        aria-label="Подробнее"
      >
        ?
      </span>
    </ViewportTooltip>
  );
}
